import {
  MicroProduct,
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { useMemo } from 'react';
import { useSalsaManifest } from '@domain/microproducts/loaders/use-salsa-manifest';

interface UsePricingMicroProductsResult {
  loading: boolean;
  products: MicroProduct[];
}

/**
 * Loads microproducts configured for the pricing sub-navigation slot.
 *
 * Mirrors the sales-hub sidebar filtering: pick the `PRICING_SIDEBAR` slot,
 * then drop any product without a deployed SPA in Salsa (avoids
 * "Product not available" pages). Until the manifest loads, show all products.
 */
export const usePricingMicroProducts = (): UsePricingMicroProductsResult => {
  const { slots, loading } = useMicroProductsLoader();
  const slotProducts = useMemo(
    () =>
      slots.find((s) => s.slot === MicroProductsSlot.PRICING_SIDEBAR)
        ?.products ?? [],
    [slots],
  );

  // Fetch the manifest from mount (not gated behind the config fetch) so it
  // resolves in parallel — same as the sales-hub loader. Gating it serializes
  // the fetches and opens a window where configured-but-undeployed products
  // render unfiltered, then vanish.
  const {
    availableProducts,
    loaded: manifestLoaded,
    loading: manifestLoading,
  } = useSalsaManifest();

  const products = useMemo(() => {
    if (!manifestLoaded) {
      return slotProducts;
    }
    return slotProducts.filter((p) => availableProducts.has(p.id));
  }, [slotProducts, availableProducts, manifestLoaded]);

  return { loading: loading || manifestLoading, products };
};
